import Axios from "axios";
import { useRemoteQuery } from "../useRemote/useRemoteQuery";
import {
  BookableBookingObjectDto,
  ResourceKind
} from "../../../features/Schedule/typings/resource-calendar.types";
import { BookingType } from "../../../features/Booking-Form/typings/booking-inputs";

export type FetchBookableBookingObjectsParams = {
  startDate: string;
  endDate: string;
  bookingType: BookingType;
  includeInactive?: boolean;
  /** Include objects the current user cannot book (resource picker). Default false. */
  includeNotPermitted?: boolean;
  resourceKind?: ResourceKind;
  locationInventoryId?: number;
};

export function bookableBookingObjectsQueryKey({
  startDate,
  endDate,
  bookingType,
  includeInactive = false,
  includeNotPermitted = false,
  resourceKind,
  locationInventoryId
}: FetchBookableBookingObjectsParams) {
  return [
    "bookableBookingObjects",
    startDate,
    endDate,
    bookingType,
    includeInactive,
    includeNotPermitted,
    resourceKind ?? "",
    locationInventoryId ?? ""
  ] as const;
}

function buildBookableBookingObjectsParams({
  startDate,
  endDate,
  bookingType,
  includeInactive = false,
  includeNotPermitted = false,
  resourceKind,
  locationInventoryId,
  hasRange
}: FetchBookableBookingObjectsParams & { hasRange: boolean }) {
  const base = {
    bookingType,
    includeInactive,
    includeNotPermitted,
    ...(resourceKind ? { resourceKind } : {}),
    ...(locationInventoryId != null ? { locationInventoryId } : {})
  };

  if (hasRange) {
    return { ...base, startDate, endDate };
  }

  return base;
}

export async function fetchBookableBookingObjects(
  params: FetchBookableBookingObjectsParams
): Promise<BookableBookingObjectDto[]> {
  const hasRange = Boolean(params.startDate?.length && params.endDate?.length);
  const { data } = await Axios.get<BookableBookingObjectDto[]>(
    "/booking-schedule/bookable-booking-objects",
    {
      params: buildBookableBookingObjectsParams({ ...params, hasRange })
    }
  );
  return data;
}

export type UseRemoteFetchBookableBookingObjectsArgs = {
  enabled: boolean;
  /** Visible range start (same semantics as schedule calendar `firstDay`), ISO-8601. */
  startDate: string | undefined;
  /** Visible range end (same as calendar `lastDay`), ISO-8601. */
  endDate: string | undefined;
  /** Optional booking type filter for reducing resource catalog payload. */
  bookingType: BookingType;
  /** Include inactive/blocked resources in the resource calendar picker. */
  includeInactive?: boolean;
  /** Include objects the current user cannot book, tagged Not permitted. Default false. */
  includeNotPermitted?: boolean;
  /** Filter resource catalog rows (`bookingType=resource`). */
  resourceKind?: ResourceKind;
  /** Server-side location filter for resource catalog. */
  locationInventoryId?: number;
};

export function useRemoteFetchBookableBookingObjects({
  enabled,
  startDate,
  endDate,
  bookingType,
  includeInactive = false,
  includeNotPermitted = false,
  resourceKind,
  locationInventoryId
}: Readonly<UseRemoteFetchBookableBookingObjectsArgs>) {
  const hasRange = Boolean(startDate?.length && endDate?.length);

  return useRemoteQuery<BookableBookingObjectDto[]>({
    queryKey: bookableBookingObjectsQueryKey({
      startDate: startDate ?? "",
      endDate: endDate ?? "",
      bookingType,
      includeInactive,
      includeNotPermitted,
      resourceKind,
      locationInventoryId
    }),
    url: "/booking-schedule/bookable-booking-objects",
    params: buildBookableBookingObjectsParams({
      startDate: startDate ?? "",
      endDate: endDate ?? "",
      bookingType,
      includeInactive,
      includeNotPermitted,
      resourceKind,
      locationInventoryId,
      hasRange
    }),
    enabled: enabled && hasRange
  });
}
